import Cookies from 'js-cookie';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { call } from 'redux-saga/effects';

import { Backstage, Payments } from '../actions';
import {
  enter404,
  enterCuratorBSP,
  enterPlaylist,
  leave404,
  leaveCuratorBSP,
  leavePlaylist,
  showCoachmark,
} from '../actions/Application';
import { setAfterLoginRedirect } from '../actions/Login';
import { openModal } from '../actions/Modal';
import { closeNowPlaying, openNowPlaying } from '../actions/NowPlaying';
import * as UseCaseActions from '../actions/UseCases';
import { ConditionalLoader } from '../components/ConditionalLoader/ConditionalLoader';
import Loading from '../components/lib/Loading/Loading';
import * as Constants from '../constants';
import { ApplicationStateKeys } from '../constants/ApplicationStateKeys';
import AppContainer from '../containers/App/AppContainer';
import * as externalUtils from '../domains/externalUtils';
import { AsyncDomain } from '../domains/index';
import { shouldNavigateToCommunity } from '../lib/ApplicationUtil';
import {
  curatorExperienceEnabled,
  getIsResponsiveSubscriptionEnabled,
  userProfile2Enabled,
} from '../lib/configuration/TunerVarUtils';
import { getAllAnnotations } from '../lib/EntityUtils';
import * as OwnUtils from '../lib/ListenerUtils';
import { isGenreMusicId } from '../lib/MusicUtils';
import {
  getAllSongsNowPlayingPath,
  getBackstageGenrePath,
  getBackstagePathFromPlaybackPath,
  getBackstagePodcastProgramPath,
  getBrowseGenreCategoryPath,
  getBrowsePath,
  getCollectionPath,
  getCommunityRedirectPath,
  getCurrentPathname,
  getCurrentPathnameFromState,
  getLandingPath,
  getLoginPath,
  getMyMusicAllFilterPath,
  getMyStationsPath,
  getNewBrowseDecadesPath,
  getNextMobilePath,
  getNotFoundPath,
  getNowPlayingPath,
  getPartnerUpgradePath,
  getRegisterPath,
  getRegistrationBlockedPath,
  getSearchPathForTermAndType,
  getStationDetailsPathForStationId,
  getStationPlayPathForStationCode,
  getUpgradeLandingPath,
  isBackstagePodcastThumbedDownEpisodesPath,
  isBackstagePodcastThumbedUpEpisodesPath,
  isInSearchPath,
  isMobileRegisterWelcomePath,
  isNowPlayingPodcastRoute,
  isOldBrowseCategoryPath,
  isOldBrowseDecadesPath,
  isOldBrowsePath,
  isPathInNowPlayingPath,
  isPodcastCollectionPath,
  mapOldBrowseToNewBrowse,
  STATION_BASE_PATH,
} from '../lib/RouteUtils';
import { getMemoizedFilterTypes } from '../lib/SourceTypeUtils';
import { putAndWait } from '../sagas/TaskManager';
import * as Selectors from '../selectors';
import Logger from '../tools/Logger';
import * as Utils from '../utils/index';

import RouteBuilder from './RouteBuilder';

// our local logger instance
const log = Logger.child({
  component: 'Routes',
  level: 'debug',
});

export const isLoggedInUser = store => () => {
  const state = store.getState();
  return OwnUtils.isLoggedIn(state) && !OwnUtils.isAnonymousListener(state);
};

const rerouteToLoginIfNeeded = store => replace => {
  const state = store.getState();

  if (!OwnUtils.isLoggedIn(state)) {
    replace(getLoginPath());
    return true;
  }

  return false;
};

const rerouteToLoginIfNotAuthenticated = (store, afterLoginRedirect) => replace => {
  const state = store.getState();

  if (!OwnUtils.isAuthenticatedListener(state)) {
    if (afterLoginRedirect) {
      store.dispatch(setAfterLoginRedirect(afterLoginRedirect));
    }

    replace(getLoginPath());
    return true;
  }

  return false;
};

/*
 * reroute authenticated users away from login and register pages
 * @param {Object} store - redux store
 * @param {Object} nextState - routerState Object
 * @param {Function} replace - use to replace current location in history
 * @returns {Boolean} true if re-routing took place false otherwise
 */
export const redirectIfAuthenticated = store => (nextState, replace) => {
  const dispatch = store.dispatch;
  const state = store.getState();
  const isPendingPurchase = Selectors.Payments.getIsPendingPurchase(state);
  if (OwnUtils.isLoggedIn(state) && !OwnUtils.isAnonymousListener(state) && !isPendingPurchase) {
    if (shouldNavigateToCommunity(state)) {
      replace(getCommunityRedirectPath());
      return true;
    }
    if (Utils.Capabilities.isMobileDevice(state)) {
      const currentPath = getCurrentPathname({ location: nextState.location });
      replace(getNextMobilePath(currentPath));
      return true;
    }
    replace(getNowPlayingPath(state));
    dispatch(showCoachmark(nextState.location.pathname));
    return true;
  }

  return false;
};

export const redirectIfAuthenticatedOrBlocked = store => (nextState, replace) => {
  const underageCookie = Cookies.get('underageblock');

  if (underageCookie) {
    replace(getRegistrationBlockedPath());
    return true;
  }

  return redirectIfAuthenticated(store)(nextState, replace);
};

// Same as redirectIfAuthenticated, but used as an onChange handler
// used if you might push the same route to the router after an action
// this will check whether a redirect is needed after that state change
export const redirectIfAuthenticatedOnChange = store => (prevState, nextState, replace) => {
  return redirectIfAuthenticated(store)(nextState, replace);
};

export const onEnterMobileAccountWelcome = store => (nextState, replace) => {
  if (!isLoggedInUser(store)()) {
    const currentPath = getCurrentPathname({ location: nextState.location });
    if (isMobileRegisterWelcomePath(currentPath)) {
      replace(getRegisterPath());
    } else {
      replace(getLoginPath());
    }
  }
};

/*
 * Is the path params attempting to create a station
 * @param params
 * @returns boolean
 */
function isNowPlayingRouteCreatingStation(params) {
  return params && (params.stationId || params.seoToken);
}

/*
 * reroute from the now playing page if needed
 * @param {Object} store - store
 * @param {Object} nextState - state
 * @param {Function} replace - use to replace current location in history
 * @returns {Boolean} true if re-routing took place false otherwise
 */
const rerouteFromNowPlayingIfNeeded = store => (nextState, replace) => {
  const state = store.getState();
  const path = getCurrentPathname({ location: nextState.location });
  const params = nextState.params;
  const isAnonymous = OwnUtils.isAnonymousListener(state);
  const isPremium = Selectors.Payments.hasPremiumPlan(state);
  const hasStation = externalUtils.getInDomain('playback.utils.listenerHasStations', false)(state);
  const hasPremiumAccessReward = externalUtils.getInDomain(
    'rewards.utils.hasActivePremiumAccess',
    false,
  )(state);

  // for premium welcome
  const showPremiumWelcome =
    isPremium &&
    !isAnonymous &&
    !OwnUtils.hasSeenWebPremiumWelcome(state) &&
    !hasPremiumAccessReward;

  const shouldShowColdStartPremiumWelcome =
    showPremiumWelcome &&
    !externalUtils.getInDomain('collection.utils.collectionHasItems', false)(state);
  if (shouldShowColdStartPremiumWelcome) {
    log.debug(
      'rerouteFromNowPlayingIfNeeded(): redirecting listener to my music for premium cold start welcome',
    );
    replace(getMyMusicAllFilterPath());
    return true;
  }

  // Prevents re-routing to landing page if its a podcast playback path
  if (isNowPlayingPodcastRoute(path)) {
    return false;
  }
  // If not routing to nowplaying with the intention to create a station and the listener
  // hasn't collected or played, route to browse, myStations or landing page.
  if (!hasStation && !isNowPlayingRouteCreatingStation(params)) {
    if (isAnonymous) {
      log.debug(
        'rerouteFromNowPlayingIfNeeded(): redirecting anon listener with no stations to landing page',
      );
      replace(getLandingPath());
    } else if (
      OwnUtils.isNonPremiumListenerLikelyFTUX(state) &&
      path.indexOf(getBrowsePath()) !== 0
    ) {
      log.debug(
        'rerouteFromNowPlayingIfNeeded(): redirecting listener with nothing collected or played to browse',
      );
      replace(getBrowsePath());
    } else if (path.indexOf(getCollectionPath()) !== 0) {
      log.debug('rerouteFromNowPlayingIfNeeded(): redirecting listener to my collection');
      replace(getCollectionPath());
    } else if (path.indexOf(getMyStationsPath()) !== 0) {
      log.debug(
        'rerouteFromNowPlayingIfNeeded(): redirecting listener with no stations to my stations',
      );
      replace(getMyStationsPath());
    }
    return true;
  }

  return false;
};

function appendWebnameIfNeeded(store, nextState, replace) {
  const state = store.getState();
  const webname = OwnUtils.getCurrentListenerFromState(state).get('webname');
  const path = getCurrentPathname({ location: nextState.location });
  const regexExpression = /\/profile($|\/thumbs$|\/(tracks|albums)$|\/following$|\/followers$)/;

  if (regexExpression.test(path)) {
    replace(`${path}/${webname}`);
    return true;
  }
  return false;
}

// '/feed' should rewrite to '/profile' as per the spec
function rewriteFeedIfNeeded(nextState, replace) {
  let path = getCurrentPathname({ location: nextState.location });
  const replaceFeed = /\/feed$/;

  if (replaceFeed.test(path)) {
    path = path.replace(replaceFeed, '/profile');
    replace(`${path}`);
    return true;
  }
  return false;
}

/*
 * @param {Object} state - store's current state
 * @param {Object} nextRouterState - state
 * @param {Function} replace - use to replace current location in history
 * @returns {Boolean} true if re-routing took place away from Now Playing false otherwise
 */
function redirectIfAtIncorrectNowPlayingPath(state, nextRouterState, replace) {
  const path = getCurrentPathname({ location: nextRouterState.location });
  const correctNowPlayingPath = getNowPlayingPath(state);
  const isInitialPageLoad =
    externalUtils.getInDomain('application.utils.getRoutedCount')(state) <= 2;

  // user loaded site on url that's used for transitioning to a new source w/o annotations yet
  if (isInitialPageLoad && !isPathInNowPlayingPath(path)) {
    replace('/');
    return true;
  }

  // user clicked the "back" button and landed on a url that's not playing anymore
  if (
    !isInitialPageLoad &&
    path !== correctNowPlayingPath &&
    correctNowPlayingPath !== STATION_BASE_PATH
  ) {
    replace(correctNowPlayingPath);
  }
  return false;
}

/*
 * @param {Object} store - store
 * @param {Object} nextState - state
 * @param {Function} replace - use to replace current location in history
 * @returns {Boolean} true if re-routing took place false otherwise
 */
const onEnterProfile = store => (nextState, replace) => {
  const state = store.getState();
  const isAnonymous = OwnUtils.isAnonymousListener(state);

  if (rerouteToLoginIfNeeded(store)(replace)) {
    return true;
  }

  if (rewriteFeedIfNeeded(nextState, replace)) {
    return true;
  }

  if (appendWebnameIfNeeded(store, nextState, replace)) {
    return true;
  }

  if (isAnonymous) {
    replace(getLandingPath());
  }

  return false;
};

/*
 * @param {Object} store - store
 * @param {Object} nextState - state
 * @param {Function} replace - use to replace current location in history
 * @returns {Boolean} true if re-routing took place false otherwise
 */
export const onEnterSettings = store => (nextState, replace) => {
  const currentPath = get(nextState, 'location.pathname', '');
  return rerouteToLoginIfNotAuthenticated(store, currentPath)(replace);
};

/*
 * reroute from the mystations page if needed
 * @param {Object} store - store
 * @param {Object} nextState - state
 * @param {Function} replace - use to replace current location in history
 * @returns {Boolean} true if re-routing took place false otherwise
 */
export const onEnterMyStations = store => (nextState, replace) => {
  if (rerouteToLoginIfNeeded(store)(replace)) {
    return true;
  }

  replace(getCollectionPath());
  return true;
};

/*
 * reroute from the my music page if needed
 * @param {Object} store - store
 * @param {Object} nextState - state
 * @param {Function} replace - use to replace current location in history
 * @returns {Boolean} true if re-routing took place false otherwise
 */
export const onEnterMyMusic = store => (nextState, replace) => {
  if (rerouteToLoginIfNeeded(store)(replace)) {
    return true;
  }

  replace(getCollectionPath());
  return true;
};

const getNewBrowseRedirectPath = pathname => {
  if (isOldBrowseDecadesPath(pathname)) {
    return getNewBrowseDecadesPath();
  } else if (isOldBrowseCategoryPath(pathname)) {
    return getBrowseGenreCategoryPath();
  }

  return mapOldBrowseToNewBrowse(pathname);
};

export const onEnterBrowse = store => (nextState, replace) => {
  const currentPath = getCurrentPathname({ location: nextState.location });

  if (isOldBrowsePath(currentPath)) {
    const nextPath = getNewBrowseRedirectPath(currentPath);
    replace(nextPath);
  }
};

const redirectToMyMusic = (nextState, replace) => {
  replace(getMyMusicAllFilterPath());
};

/*
 * reroute from collection page
 * @param {Object} store - store
 * @param {Object} nextState - state
 * @param {Function} replace - use to replace current location in history
 */
export const onEnterCollection = store => (nextState, replace) => {
  const state = store.getState();
  const isAnonymous = OwnUtils.isAnonymousListener(state);
  const hasNoContent = !externalUtils.getInDomain(
    'collection.utils.collectionHasItems',
    false,
  )(state);
  // user has no stations, and is anonymous
  const isAnonWithNoStations =
    !externalUtils.getInDomain('playback.utils.listenerHasStations', false)(state) && isAnonymous;
  const isAnonWithEmptyCollection = hasNoContent && isAnonWithNoStations;

  if (rerouteToLoginIfNeeded(store)(replace) || isAnonWithEmptyCollection) {
    replace(getLoginPath());
    return true;
  }

  const isPremium = OwnUtils.hasPremiumFeatures(state);
  const availableFilters = getMemoizedFilterTypes(isPremium, false);
  const filterUnavailableForTier =
    nextState.params.filter && !availableFilters.includes(nextState.params.filter);
  const artistPathUnavailableForTier = nextState.params.artistId && !isPremium;

  if (filterUnavailableForTier || artistPathUnavailableForTier) {
    replace(getCollectionPath());
  }

  return false;
};

/*
 * Check landing endpoint and redirect if necessary
 * @param {Object} store - store
 * @param {Object} nextState - state
 * @param {Function} replace - use to replace current location in history
 * @returns {Boolean} true if re-routing took place false otherwise
 */
function landingTransitionHandler(store, nextState, replace) {
  const state = store.getState();
  const path = getCurrentPathname({ location: nextState.location });

  /* app should be initialized by this point */
  const isInitialized = state.application.get(ApplicationStateKeys.APPLICATION_INITIALIZED);
  if (!isInitialized) {
    return false;
  }

  if (Utils.Capabilities.isMobileDevice(state)) return;

  if (path === getLandingPath()) {
    /* are we a musicId launch URL? re-route to playback URL */
    const musicId = Utils.QueryParams.get(
      state,
      Constants.App.StartupQueryParameterKeys.START_STATION_BY_MUSIC_ID,
    );

    /* play routes with 'sc' key could have a stationId, a musicId or an encrypted stationId */
    const stationId = Utils.QueryParams.get(
      state,
      Constants.App.StartupQueryParameterKeys.START_STATION_BY_CODE,
    );

    let stationPlayPathToRedirect;

    if (musicId) {
      const stationCode = Constants.Station.StationConstants.MUSIC_ID + musicId;
      stationPlayPathToRedirect = getStationPlayPathForStationCode(stationCode);
    }

    if (stationId) {
      stationPlayPathToRedirect = getStationPlayPathForStationCode(stationId);
    }

    if (rerouteToLoginIfNeeded(store)(replace)) {
      stationPlayPathToRedirect && store.dispatch(setAfterLoginRedirect(stationPlayPathToRedirect));
      return true;
    }

    if (stationPlayPathToRedirect) {
      replace(stationPlayPathToRedirect);
      return true;
    }

    // landing path is only allowed for an anon listener with no previously created stations,
    // otherwise route to now playing.
    if (
      !(
        OwnUtils.isAnonymousListener(state) &&
        !externalUtils.getInDomain('playback.utils.listenerHasStations', false)(state)
      )
    ) {
      replace(getNowPlayingPath(state));
      return true;
    }

    if (OwnUtils.hasLoggedIn(state) && rerouteToLoginIfNotAuthenticated(store)(replace)) {
      return true;
    }
  }

  return false;
}

export const onChangeLanding = store => (state, nextState, replace) => {
  return landingTransitionHandler(store, nextState, replace);
};

export const onEnterLanding = store => (nextState, replace) => {
  return landingTransitionHandler(store, nextState, replace);
};

/*
 * Handler for deprecated History route
 * @param {Object} store - redux store
 * @param {Object} nextState - state
 * @param {Function} replace - use to replace current location in history
 */
const onEnterNowPlayingSessionHistory = () => (nextState, replace, transition) => {
  replace(getStationPlayPathForStationCode(nextState.params.stationId));
  transition();
};

/*
 * Called when route
 * @param {Object} store - store
 * @param {Object} nextState - state
 * @param {Function} replace - use to replace current location in history
 * @param {[type]} transition - [description]
 */
const onEnterNowPlaying = store => (nextState, replace, transition) => {
  const pathname = nextState.location.pathname;

  if (rerouteToLoginIfNeeded(store)(replace)) {
    store.dispatch(setAfterLoginRedirect(pathname));
    transition();
    return;
  }
  if (rerouteFromNowPlayingIfNeeded(store)(nextState, replace)) {
    /* we're re-routing, notify to transition */
    transition();
    return;
  }
  const state = store.getState();

  const currentStationId = externalUtils.getInDomain('playback.utils.getCurrentStationId')(state);
  if (pathname === '/station' && currentStationId) {
    replace(Utils.Route.getStationPlayPathForStationCode(currentStationId));
  }

  const leftNowPlaying = redirectIfAtIncorrectNowPlayingPath(state, nextState, replace);
  if (leftNowPlaying) {
    transition();
    return;
  }
  store.dispatch(openNowPlaying()).then(() => transition());
};

/*
 * Called by router when user leaves now playing view. Fires action creators to make needed UI updates.
 */
const onLeaveNowPlaying = store => () => {
  store.dispatch(closeNowPlaying());
};

export const redirectToStationDetails = store => (nextState, replace, stationId) => {
  const id = nextState.params.stationId || stationId;
  const state = store.getState();

  const isThumbprintRadio = externalUtils.getInDomain('playback.utils.isThumbprintStationId')(
    state,
    id,
  );
  if (!isThumbprintRadio) {
    replace(getStationDetailsPathForStationId(id));
    return true;
  }
  return false;
};

export const onEnterPodcastBSP = store => (nextState, replace, transition) => {
  const state = store.getState();
  const url = window.location.pathname;
  const isOnThumbsPage =
    isBackstagePodcastThumbedDownEpisodesPath(url) || isBackstagePodcastThumbedUpEpisodesPath(url);
  const isMobileDevice = Utils.Capabilities.isMobileDevice(state);

  if (isOnThumbsPage && isMobileDevice) {
    const { programName, podcastId } = nextState.params;
    replace(getBackstagePodcastProgramPath({ programName, podcastId }));
  }

  transition();
};

export const onEnterCollectedStationBSP = store => (nextState, replace, transition) => {
  const state = store.getState();
  const stationId = get(nextState, 'params.stationId', null);
  const isOwnStation = externalUtils.getInDomain('playback.utils.isOwnStation', false)(
    state,
    stationId,
  );

  // escape hatch if invalid station id - WEBCA-1591
  if (!isOwnStation) {
    replace(getNotFoundPath());
  }

  transition();
};

export const onEnterBackstage = store => (nextState, replace, transition) => {
  if (isGenreMusicId(nextState.params.genreId)) {
    const state = store.getState();
    const station = externalUtils.getInDomain('playback.utils.createGetStationByMusicIdSelector')(
      state,
    )(nextState.params.genreId);
    const stationId = station && station.get('stationId');
    if (station && externalUtils.getInDomain('playback.utils.isOwnStation')(state, stationId)) {
      if (redirectToStationDetails(store)(nextState, replace, stationId)) {
        transition();
        return;
      }
    }
  }

  store.dispatch(Backstage.enterBackstage(nextState));
  transition();
};

export const onLeaveBackstage = store => () => {
  store.dispatch(Backstage.leaveBackstage());
};

export const redirectPlaybackToBackstage = store => (nextState, replace) => {
  const currentPath = getCurrentPathname({ location: nextState.location });
  const state = store.getState();
  const annotations = getAllAnnotations(state);
  const backstagePath = getBackstagePathFromPlaybackPath(currentPath, annotations);
  replace(backstagePath);
};

/**
 * error handler for dynamic component loading
 *
 * @param  {string} err - the error during load
 */
export function errorLoading(err) {
  log.error(err, 'Dynamic component loading failed');
}

const onEnter404 = store => () => {
  store.dispatch(enter404());
};

const onLeave404 = store => () => {
  store.dispatch(leave404());
};

export const isResponsiveSubscriptionEnabledWrapper = store => () => {
  const state = store.getState();
  return getIsResponsiveSubscriptionEnabled(state);
};

export const onEnterSubscriptionManagement = store => (nextState, replace, callback) => {
  // The `enterSubscriptionManagement` action has a potential redirect to /upgrade which happens all in one synchronous
  // thread. This results in the Subscription component being rendered AFTER the upgrade component. Calling `runSaga()`
  // here makes sure we wait for the handling of the `onEnterSubscriptionManagement` onEnter function to complete
  // and then it processes the /upgrade route. As a result we prevent the Subscription component from rendering or
  // even loading. [ref: WEBCLIENT-7027]
  store.runSaga(function* () {
    yield call(putAndWait, Payments.enterSubscriptionManagement());
    callback();
  });
};

export const onEnterFamilyPlanManagement = store => nextState => {
  const currentPath = get(nextState, 'location.pathname', '');
  store.dispatch(Payments.enterFamilyPlanManagement({ currentPath }));
};

/*
 * reroute partner upgrade users to either login/register pages or custom partner upgrade welcome modal
 * @param {Object} store - redux store
 */
export const onEnterPartnerUpgradeFlow = store => () => {
  store.dispatch(Payments.enterPartnerUpgradeFlow());
};

export const redirectToPartnerUpgrade = () => (nextState, replace) => {
  replace(getPartnerUpgradePath());
};

const onEnterOrChangeUpgradeFlow = isOnChange => store => (state1, state2) => {
  let nextState = null;
  if (isOnChange) {
    nextState = state2;
  } else {
    nextState = state1;
  }

  const tierSlug = get(nextState, 'params.tierSlug');
  const subTierSlug = get(nextState, 'params.subTierSlug');
  store.dispatch(
    UseCaseActions.upgrade({
      tierSlug,
      subTierSlug,
    }),
  );
};

export const onEnterUpgradeFlow = onEnterOrChangeUpgradeFlow(false);

export const onChangeUpgradeFlow = onEnterOrChangeUpgradeFlow(true);

export const onEnterVerification = store => nextState => {
  const tierSlug = get(nextState, 'params.tierSlug');
  const verificationType = get(nextState, 'params.verificationType');

  store.dispatch(
    UseCaseActions.upgrade({
      tierSlug,
      subTierSlug: verificationType,
    }),
  );
};

export const onEnterMobileMessage = store => (nextState, replace) => {
  if (!isLoggedInUser(store)()) {
    replace(getLoginPath());
    const currentPath = get(nextState, 'location.pathname', '');
    store.dispatch(setAfterLoginRedirect(currentPath));
  }
};

export const onEnterPaypalCancel = store => (nextState, replace) => {
  store.dispatch(Payments.setPaypalComplete());
  replace(getUpgradeLandingPath());
};

export const onEnterPaypalComplete = store => () => {
  store.dispatch(Payments.enterPaypalComplete());
};

export const onEnterPlans = store => (nextState, replace) => {
  if (isLoggedInUser(store)()) {
    replace(getUpgradeLandingPath());
  } else {
    store.dispatch(Payments.clearSelectedOffer());
  }
};

/*
 * Backwards compatible routing to match classic routing
 *
 * @param nextState
 * @param replace
 */
function onBrowseCatalog(nextState, replace) {
  const category = parseInt(nextState.params.category, 10);
  if (isNumber(category) && !isNaN(category)) {
    /* if browse/catalog/<module>/<number> or browse/catalog/<number>, redirect to /browse.
      This route type is a mobile clients impl but web handles by routing to browse */
    replace(getBrowsePath());
    return;
  }

  /* redirect to category */
  replace(getBrowseGenreCategoryPath(nextState.params.category));
}

export const onEnterPodcastBrowse = () => (nextState, replace) => {
  return onBrowseCatalog(nextState, replace);
};

const redirectToGenreBackstage = () => (nextState, replace) => {
  replace(getBackstageGenrePath(nextState.params.stationToken));
};

const hasPremiumFeatures = store => () => {
  const state = store.getState();
  return OwnUtils.hasPremiumFeatures(state);
};

const onMountSearchT3 = (store, router, params) => {
  if (
    !isInSearchPath(router.getCurrentLocation().pathname) ||
    get(router, 'location.action') === Constants.App.RouteActions.REPLACE
  ) {
    return;
  }

  const { searchTerm, searchFilter } = params;
  const searchTypes = Object.values(Constants.SourceTypes.SearchT3Types);

  if (searchTerm && !includes(searchTypes, searchFilter)) {
    router.replace(getSearchPathForTermAndType(searchTerm, 'all'));
  }

  if (searchFilter === 'tracks') {
    router.replace(getSearchPathForTermAndType(searchTerm, 'songs'));
  }

  store.dispatch(
    UseCaseActions.openSearch({
      searchTerm,
      searchType: externalUtils.getInDomain(
        'searchT3.constants.SearchKeys.MAIN_SEARCH',
        'mainSearch',
      ),
      searchFilter,
      routeEntry: true,
    }),
  );
};

const legacyToRedesignRoutes = {
  '/account/info': '/settings/info',
  '/account/privacy': '/settings/info',
  '/account/notices': '/settings/notices',
  '/account/artistMessages': '/settings/content',
  '/account/audioQuality': '/settings/content',
  '/account/upgrade': '/settings/upgrade',
  '/account/billing': '/settings/subscription',
};

export function onEnterLegacyRoute(nextState, replace) {
  const pathname = nextState.location.pathname;
  const newLocation = legacyToRedesignRoutes[pathname] || getLandingPath();
  replace(newLocation);
  return true;
}

function onEnterStationLanding(nextState, replace) {
  const stationId = nextState.params.stationId;
  replace(getStationPlayPathForStationCode(stationId));
}

const onEnterFirstIntro = store => (nextState, replace) => {
  const dispatch = store.dispatch;
  replace(getLandingPath());
  dispatch(
    openModal({
      modalType: 'ModalStandard',
      title: <FormattedMessage {...Constants.FirstIntro.messages.notAvailableHeader} />,
      body: <FormattedMessage {...Constants.FirstIntro.messages.notAvailableBody} />,
    }),
  );
};

const hidePodcastCollection = store => nextState => {
  const state = store.getState();
  const pathname = getCurrentPathnameFromState(state);
  return isPodcastCollectionPath(pathname);
};

export function createConditionalComponent(config) {
  return class extends React.Component {
    shouldComponentUpdate() {
      return false;
    }

    render() {
      const notFoundConfig = {
        // Caution! When setting a custom importer your config be sure not to chain a `then()` after System.import
        // that calls `cb(null, module.default)` as this messes up our use of React.lazy in AsyncComponent. However
        // we can continue to use this pattern outside of createConditionalComponent().
        importer: () => {
          return System.import(
            /* webpackChunkName: "c_not-found" */ `${BASE_PATH}/src/containers/NotFound/NotFound`,
          );
        },
        component: 'NotFound',
      };
      const configToPass = Object.assign(
        {},
        {
          ifTrueOptions: notFoundConfig,
          ifFalseOptions: notFoundConfig,
        },
        config,
      );
      const Component = ConditionalLoader(configToPass, this.props);
      return <Component />;
    }
  };
}

export const onEnterPlaylist = store => () => {
  store.dispatch(enterPlaylist());
};

export const onLeavePlaylist = store => () => {
  store.dispatch(leavePlaylist());
};

export const onEnterCuratorBSP = store => () => {
  store.dispatch(enterCuratorBSP());
};

export const onLeaveCuratorBSP = store => () => {
  store.dispatch(leaveCuratorBSP());
};

// TODO: WEBCA-1926 remove the curator experience flag once the feature has been released to prod
export const isCuratorExperienceEnabled = store => () => {
  const state = store.getState();
  return curatorExperienceEnabled(state);
};

const onEnterNowPlayingOnDemand = store => (nextState, replace) => {
  const state = store.getState();
  const isPremiumEnabled = OwnUtils.hasPremiumFeatures(state);

  if (redirectIfAtIncorrectNowPlayingPath(state, nextState, replace)) {
    return;
  }

  if (!isPremiumEnabled) {
    redirectPlaybackToBackstage(store)(nextState, replace);
    return;
  }

  store.dispatch(openNowPlaying());
};

const onEnterPodcastNowPlaying = store => transition => {
  store.dispatch(openNowPlaying()).then(() => transition());
};

const onEnterAllSongsNowPlayingShortRoute = artistPrefix => (store, router, params) => {
  const { artistName, artistPandoraIdWithoutPrefix } = params;
  const pandoraId = artistPrefix + artistPandoraIdWithoutPrefix;
  const slugPlusPandoraId = `/${artistName}/${pandoraId}`;
  const path = getAllSongsNowPlayingPath(slugPlusPandoraId);
  router.replace(path);
};

export const userProfile2EnabledWrapper = store => () => {
  const state = store.getState();
  return userProfile2Enabled(state);
};

export default store =>
  RouteBuilder.webRouteBuilder({
    builderObj: {
      isLoggedInUser: isLoggedInUser(store),
      errorLoading,
      redirectIfAuthenticated: redirectIfAuthenticated(store),
      redirectIfAuthenticatedOrBlocked: redirectIfAuthenticatedOrBlocked(store),
      redirectIfAuthenticatedOnChange: redirectIfAuthenticatedOnChange(store),
      onEnterUpgradeFlow: onEnterUpgradeFlow(store),
      onChangeUpgradeFlow: onChangeUpgradeFlow(store),
      onEnterSubscriptionManagement: onEnterSubscriptionManagement(store),
      onEnterFamilyPlanManagement: onEnterFamilyPlanManagement(store),
      onEnterPartnerUpgradeFlow: onEnterPartnerUpgradeFlow(store),
      onEnterLanding: onEnterLanding(store),
      onChangeLanding: onChangeLanding(store),
      onEnterNowPlaying: onEnterNowPlaying(store),
      onEnterPodcastNowPlaying: onEnterPodcastNowPlaying(store),
      onLeaveNowPlaying: onLeaveNowPlaying(store),
      onEnterNowPlayingSessionHistory: onEnterNowPlayingSessionHistory(store),
      onEnterMyStations: onEnterMyStations(store),
      onEnterProfile: onEnterProfile(store),
      onEnterSettings: onEnterSettings(store),
      onEnterBackstage: onEnterBackstage(store),
      onLeaveBackstage: onLeaveBackstage(store),
      onEnter404: onEnter404(store),
      onLeave404: onLeave404(store),
      onBrowseCatalog,
      onEnterBrowse: onEnterBrowse(store),
      redirectToGenreBackstage: redirectToGenreBackstage(store),
      onMountSearchT3,
      redirectToStationDetails: redirectToStationDetails(store),
      onEnterLegacyRoute,
      onEnterStationLanding,
      onEnterFirstIntro: onEnterFirstIntro(store),
      createConditionalComponent,
      hasPremiumFeatures: hasPremiumFeatures(store),
      hidePodcastCollection: hidePodcastCollection(store),
      onEnterPlaylist: onEnterPlaylist(store),
      onLeavePlaylist: onLeavePlaylist(store),
      onEnterCuratorBSP: onEnterCuratorBSP(store),
      onLeaveCuratorBSP: onLeaveCuratorBSP(store),
      isCuratorExperienceEnabled: isCuratorExperienceEnabled(store),
      onEnterPodcastBSP: onEnterPodcastBSP(store),
      onEnterCollectedStationBSP: onEnterCollectedStationBSP(store),
      AsyncDomain,
      onEnterNowPlayingOnDemand: onEnterNowPlayingOnDemand(store),
      userProfile2Enabled: userProfile2EnabledWrapper(store),
      onEnterMyMusic: onEnterMyMusic(store),
      onEnterCollection: onEnterCollection(store),
      redirectToPartnerUpgrade: redirectToPartnerUpgrade(store),
      AppContainer,
      onEnterAllSongsNowPlayingShortRoute,
      redirectToMyMusic,
      onEnterPodcastBrowse: onEnterPodcastBrowse(store),
      redirectPlaybackToBackstage: redirectPlaybackToBackstage(store),
      onEnterVerification: onEnterVerification(store),
      onEnterMobileMessage: onEnterMobileMessage(store),
      onEnterPlans: onEnterPlans(store),
      onEnterPaypalCancel: onEnterPaypalCancel(store),
      onEnterPaypalComplete: onEnterPaypalComplete(store),
      Loading,
    },
  });
